import { bindable } from "aurelia-templating";
import { Guid } from "../../../utilities/guid";
import { autoinject, transient } from "aurelia-dependency-injection";
import { QueriesClientService } from "../../../services/queries/queries-client.services";
import { GridUtils, EditableFieldBinding } from "../data-grid/data-grid";
import {
    AndOrModel, EntityFilterPropertyModel, EntityMetaDataModel,
    EntityMetaDataModelOperator, QueryFilter, DataType, EntityProjectionPropertyModel, GetOperatorsResponse
} from "../../../models/queries/queries.models";
import * as Enumerable from "linq";
import { BindingSignaler } from "aurelia-templating-resources";
import { BindingEngine, Disposable, computedFrom } from "aurelia-binding";
import { Subscription } from "aurelia-event-aggregator";
import { ArrayUtils } from "../../../utilities/arrays";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { I18N } from 'aurelia-i18n';
import { AllEntityTypeConfigurationConstants, QueryLookupsWithContext } from "../../../models/basics/basics.models";
import { UserClientService } from "../../../services/authentication/user-client.service";
import { AggregateOperator } from "../../../models/rules/rules.models";
import { NotifyPropertyChangedBase, Reflection } from "../../../utilities/reflection";

@autoinject()
@transient()
export class QueryBuilder extends NotifyPropertyChangedBase{

    protected funcsQueue: any[] = [];
    private readonly colors: string[] = ["#37ade7", "#ff6c4f", "#fec310", "#df5282", "#447092", "#955075", "#7482ed"];
    public selectedModel: EntityMetaDataModel;
    protected subscriptionForClausesCollectionChanges: Subscription;
    private _subscriptions: Disposable[] = [];
    protected promises: Promise<any>[];
    public count: number = 0;
    public metaDataLoaded: boolean = false;
    public isPropertySelected: { property: EntityFilterPropertyModel, isSelected: boolean }[] = [];
    public operators: GetOperatorsResponse;
    public models: EntityMetaDataModel[];
    public readonly andOr: AndOrModel[] = [
        { name: "And", isAnd: true },
        { name: "Or", isAnd: false }
    ];
    private queryBuilderEl: HTMLElement;

    public context: any;
    public isAggregate: boolean = false;

    @bindable
    public propValue: string = "";

    @bindable
    public propName: string = "uri";

    @bindable
    public sortAlphabetically: boolean = true;

    @bindable
    public data: any = null;

    @bindable
    public readonly: boolean = false;

    @bindable
    public addNewClauseLabel: string = this.i18N.tr('add-new-clause-text');

    @bindable
    public clauses: QueryFilter[] = [];

    @bindable
    public propertiesToHide: string[] = [];

    public constructor(private queriesService: QueriesClientService,
        private bindingEngine: BindingEngine,
        private element: Element,
        private bindingSignaler: BindingSignaler,
        private userClientService: UserClientService,
        private i18N: I18N) {
            super();
    }
 
    detached() {
        this.disposeSubscriptions();
    }

    disposeSubscriptions() {
        if (this.subscriptionForClausesCollectionChanges) {
            this.subscriptionForClausesCollectionChanges.dispose();
            this.subscriptionForClausesCollectionChanges = null; 
        } 
        if (this._subscriptions && this._subscriptions.length > 0) {
            this._subscriptions.forEach(x => x.dispose());
            this._subscriptions = [];
        }
    }

    public validate(): boolean {
        this.clauses.forEach((x, index) => {
            this.ensureIsPropertySelectedObjectIsValid();
            this.isPropertySelected[index].isSelected = x.filterProperty != null;
        });
        return !this.isPropertySelected.some(x => !x.isSelected);
    }

    public readonlyChanged(): void {
        this.bindingSignaler.signal("update-filters-html");
        this.bindingSignaler.signal("update-operators");
    }

    public propNameChanged(): void {
        this.bind(this.context);
    }

    public propValueChanged(): void {
        this.bind(this.context);
    }

    public getPropertyFriendlyName = (item: EntityFilterPropertyModel) => {
        if (!this.selectedModel || !this.selectedModel.filterProperties) {
            return null;
        }
        const prop = this.selectedModel.filterProperties.filter(x => x.name == item.name)[0];
        if (!prop) {
            return null;
        }
        return prop.friendlyName;
    }

    public getPropertyOperators(filter: QueryFilter) {
        if (!this.operators || !filter || !filter.filterProperty) {
            return [];
        }

        let operators: EntityMetaDataModelOperator[] = [];
        if (filter.filterProperty.isCollection) {
            operators = this.operators.operatorsForCollections[filter.filterProperty.type];
        } else {
            operators = this.operators.operatorsForSingleValues[filter.filterProperty.type];
        }

        if (!filter.filterProperty.isNullable) {
            operators = operators.filter(x => !x.isOnlyForNullableProperties);
        }
        return operators;
    }

    filterOperatorChanged($event) {
        if ($event && $event.detail) {
            this.bindingSignaler.signal("update-filters-html");
        }
    }

    public bind(bindingContext: any): void {
        this.context = bindingContext;
        this.clausesChanged();
        this.count++;
        const count = this.count;
        this.promises =
            [
                this.queriesService.getQueryOperators(),
                this.queriesService.getEntityModels()
            ];
        Promise.all(this.promises).then(results => {
            if (count === this.count && this.propValue) {
                this.operators = results[0];
                this.models = results[1];
                let selectedModel = Enumerable.from(this.models).firstOrDefault(x => x[this.propName] && x[this.propName].toString().toLowerCase() === this.propValue.replace(/-/g, "").toString().toLowerCase());
                const propertiesToHide = this.propertiesToHide ? this.propertiesToHide : [];
                const filterProperties = [];
                selectedModel = selectedModel ? CloneHelper.cloneObject(selectedModel) : selectedModel;
                selectedModel.filterProperties.forEach(x => {
                    filterProperties.push(CloneHelper.clone(x));
                });
                selectedModel.filterProperties = filterProperties.filter(x => propertiesToHide.every(y => y !== x.name));

                this.dispatchEvent("before-selected-model-changed", { oldModel: this.selectedModel, newModel: selectedModel });

                this.selectedModel = selectedModel;
                if (this.sortAlphabetically) {
                    this.sortPropertiesOfSelectedModel();
                }
                setTimeout(() => {
                    this.promises = null;
                    this.funcsQueue.forEach(x => {
                        x();
                    });
                    this.funcsQueue = [];
                    this.dispatchEvent("selected-model-changed", this.selectedModel);
                    this.metaDataLoaded = true;
                });
            }
        });
    }

    public isReady(): boolean {
        return this.promises == null;
    }

    public resetCount(): Promise<any> {
        return new Promise<void>((resolve, reject) => {
            if (this.isReady()) {
                this.count = 0;
            } else {
                reject();
            }
        });
    }

    public clausesChanged(): void { 
        this.disposeSubscriptions();
        this.isPropertySelected = [];
        if (this.clauses) { 
            this.clauses.forEach(x => {
                const sub = this.bindingEngine.propertyObserver(x, "selectedForGrouping").subscribe(() => {
                    this.onPropertyChanged<QueryBuilder>(z => z.clauses)
                });
                this._subscriptions.push(sub);
                if(this.selectedModel) {
                    x.isAnd = x.isAnd == null ? true : x.isAnd; 
                }
            });
            
            this.subscriptionForClausesCollectionChanges = this.bindingEngine.collectionObserver(this.clauses).subscribe((changedValues: any) => {
                 
                if (changedValues && changedValues[0]) {
                    const countNew: number = changedValues[0].addedCount;
                    const startIndex: number = changedValues[0].index;
                    const removed: any[] = changedValues[0].removed;
                    removed.forEach(x => {
                        const index = ArrayUtils.findIndex(this.isPropertySelected, y => y == x);
                        this.isPropertySelected.splice(index, 1);
                    });
                    const addedValues: any[] = this.clauses.slice(startIndex, startIndex + countNew);
                    addedValues.forEach(x => {
                        this.isPropertySelected.push({ property: x.filterProperty, isSelected: true });
                        const sub = this.bindingEngine.propertyObserver(x, "selectedForGrouping").subscribe(() => {
                            this.onPropertyChanged<QueryBuilder>(z => z.clauses)
                        })
                        this._subscriptions.push(sub);
                    }); 
                }
            });
        }
    }

    public addFilter(index?: number): void {
        if (!this.promises) {
            const newFilter = new QueryFilter();
            newFilter.isAggregate = this.isAggregate;
            if (this.isAggregate)
                newFilter.aggregateOperator = AggregateOperator.Count;
            newFilter.isAnd = true;
            if (!isNaN(index) && this.clauses.length > index) {
                this.clauses.splice(index + 1, 0, newFilter);
            } else {
                this.clauses.push(newFilter);
                setTimeout(() => this.scrollToBottom(), 100)
            }
        } else {
            this.funcsQueue.push(() => this.addFilter(index));
        }
    }

    public onPropertySelected($event, $index, filter: QueryFilter): void {
        this.ensureIsPropertySelectedObjectIsValid();
        this.isPropertySelected[$index].isSelected = true;
        filter.filterValue = undefined;
        const contextProperty = QueryLookupsWithContext.filter(x => filter.filterProperty && filter.filterProperty.lookupMappedToProperty && x.lookup.toLowerCase() == filter.filterProperty.lookupMappedToProperty.toLowerCase())[0];
        if (contextProperty) {
            filter.filterContextPropertyValue = this.userClientService.currentUser[contextProperty.contextProperty];
        }
        setTimeout(() => {
            this.bindingSignaler.signal("update-operators");
            this.bindingSignaler.signal("update-filters-html");
        });
    }

    public removeFilter(item: QueryFilter): void {
        const index = this.clauses.indexOf(item);
        if (this.clauses[index].subscription && this.clauses[index].subscription.dispose) {
            this.clauses[index].subscription.dispose();
        }

        if (index > -1) {
            this.clauses.splice(index, 1);
            if (this.isPropertySelected[index]) {
                this.isPropertySelected[index].isSelected = true;
            }
        }

        if (this.clauses.length == 0) {
            this.addFilter(0);
        }
    }
 
    @computedFrom(Reflection.getPropertyPath<QueryBuilder>(z => z.clauses),
        Reflection.getPropertyPath<QueryBuilder>(z => z.changes.clauses), 
        Reflection.getPropertyPath<QueryBuilder>(z => z.clauses.length))
    public get shouldShowGroupingAction(): boolean {
        let count = 0;
        if (this.clauses) {

            for (const item of this.clauses) {
                if (item.selectedForGrouping) {
                    if (item.groupGuid) {
                        return false;
                    }
                    count++;
                }
            }
        }

        if (count > 1) {
            return this.canGroup();
        }

        return false;
    }
    
    @computedFrom(Reflection.getPropertyPath<QueryBuilder>(z => z.clauses),
        Reflection.getPropertyPath<QueryBuilder>(z => z.changes.clauses), 
        Reflection.getPropertyPath<QueryBuilder>(z => z.clauses.length))
    public get shouldShowUnGroupingAction(): boolean {
        let count = 0;
        let guid = null;
        if (this.clauses) {
            for (const item of this.clauses) {
                if (item.selectedForGrouping) {
                    count++;
                }

                if (item.selectedForGrouping && item.groupGuid) {
                    guid = item.groupGuid;
                }
            }
        }

        if (count >= 1) {
            if (count === 1 && Enumerable.from(this.clauses).count(x => x.groupGuid === guid) === 2) {
                return false;
            }

            return !this.canGroup();
        }

        return false;
    }

    public canGroup(): boolean {
        const inGroup: QueryFilter[] = [];
        const notInGroup: QueryFilter[] = [];
        if (this.clauses) {

            for (const item of this.clauses) {
                if (item.selectedForGrouping) {
                    if (item.groupGuid && item.groupGuid !== "") {
                        inGroup.push(item);
                    } else {
                        notInGroup.push(item);
                    }
                }
            }
        }

        return notInGroup.length !== 0;
    }

    public groupFilters(): void {

        const groupColor = this.pickColorClass();
        const groupGuid = Guid.generateGuid();
        for (const item of this.clauses) {
            if (item.selectedForGrouping) {
                item.groupColor = groupColor;
                item.groupGuid = groupGuid;
                item.selectedForGrouping = false;
            }
        }

        this.onPropertyChanged<QueryBuilder>(z => z.clauses); 
        this.reorderList();
    }

    public reComputeIsLast(): void {
        // if the collection only has 1 left make it as is first
        let counter = 0;
        for (const item of this.clauses) {
            item.isLast = false;
            if (counter === this.clauses.length - 1) {
                item.isLast = true;
            }
            counter++;
        }
    }

    public onGroupFilterClick(): void {
        const inGroup: QueryFilter[] = [];
        const notInGroup: QueryFilter[] = [];
        for (const item of this.clauses) {
            if (item.selectedForGrouping) {
                if (item.groupGuid && item.groupGuid !== "") {
                    inGroup.push(item);
                } else {
                    notInGroup.push(item);
                }
            }
        }
        if (notInGroup.length === 0) {
 
            for (const item of inGroup) {
                item.groupColor = null;
                item.groupGuid = null;
                item.selectedForGrouping = false;
            }
            this.onPropertyChanged<QueryBuilder>(z => z.clauses); 
            this.reorderList();
        } else {
            this.groupFilters();
        } 
    }

    public filtersHtml(filter: QueryFilter): string {
        if (!filter || !filter.filterProperty) {
            return null;
        }

        filter.skipOnNullOrEmpty = !filter.filterProperty.isDynamicProperty && !filter.filterProperty.isNullable;

        const projectedProperty = this.selectedModel && this.selectedModel.projectionProperties
            ? this.selectedModel.projectionProperties.filter(x => x.name == filter.filterProperty.name)[0]
            : null;

        //when datatype is lookup and in is selected
        const isValueCollection = filter.filterProperty.type == DataType.Lookup && filter.filterOperator && ["in", "not in"].indexOf(filter.filterOperator.friendlyName) > -1;
        const isEntityFinder = filter.filterProperty.type == DataType.EntityFinder;// filter.filterProperty.entityFinderMappedToProperty ? true : false;

        let query = "filter.filterProperty.lookupMappedToProperty";
        let lookup = filter && filter.filterProperty ? filter.filterProperty.lookupMappedToProperty : null;
        let value = "filter.filterProperty.lookupMappedToProperty";
        let dataType = filter.filterProperty.type == DataType.Number && filter.filterOperator && ["in", "not in"] ? DataType.Default :
            filter.filterProperty.type;

        if (isEntityFinder) {
            lookup = filter && filter.filterProperty ? filter.filterProperty.entityFinderMappedToProperty : null;
            query = "filter.filterProperty.entityFinderMappedToProperty";
            value = "filter.filterProperty.entityFinderMappedToProperty";
            dataType = DataType.EntityFinder;
        }

        filter["initFilterData"] = {
            type: filter.filterProperty.type,
            contextPropertyValue: filter.filterContextPropertyValue,
            lookup: filter.filterProperty.lookupMappedToProperty,
            entityFinder: filter.filterProperty.entityFinderMappedToProperty,
            tags: filter.filterProperty.tags
        };

        const binding: EditableFieldBinding = {
            query: query,
            lookup: lookup,
            isEditable: !this.readonly && !filter.isReadonly,
            binding: `filter.filterValue | initFilter : filter.initFilterData`,
            displayValueBinding: "filter.filterValue",
            value: value,
            dataType: dataType,
            parameter: "filter.filterValue",
            isValueCollection: isValueCollection,
            isPropertyCollection: filter.filterProperty.isCollection,
            secondParameter: this.getSecondParameter(filter, projectedProperty),
            isNotRangeDatePicker: true,
            isSecondParameterBindable: false,
            startLabel: filter.filterProperty.startLabel,
            endLabel: filter.filterProperty.endLabel,
            isNullable: filter.filterProperty.isNullable
        };

        return GridUtils.getControl(binding, true);
    }

    public getEntityFinderTypes(uri: string) {
        return AllEntityTypeConfigurationConstants.filter(x => x.uri == uri);
    }

    private getSecondParameter(filter: QueryFilter, projectedProperty: EntityProjectionPropertyModel) {

        if (filter.filterProperty.type == DataType.Lookup || filter.filterProperty.type == DataType.HierarchicalLookup) {
            return filter.filterProperty.lookupParametersMappedToProperty;
        }

        if (!projectedProperty) {
            return "";
        }
        const type = filter.filterProperty.type;
        return type == DataType.Date || type == DataType.DateTime ? projectedProperty.format : "";
    }

    private getRandomInt(min, max): number {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    private reorderList(): void {
        if (this.clauses) {
            const groupedFilters = Enumerable.from(this.clauses)
                .groupBy((filter) => filter.groupGuid, (filter) => filter)
                .selectMany(x => x ? x.toArray() : []).toArray();
            if(this.clauses != null) {
                this.clauses.splice(0, this.clauses.length, ...groupedFilters);
            }
            else this.clauses = groupedFilters;
            this.reComputeIsLast();
        }
    }

    private pickColorClass(): string {
        const selectedColor = this.colors[this.getRandomInt(0, this.colors.length - 1)];
        return selectedColor;
    }

    private sortPropertiesOfSelectedModel(): void {
        for (let i = 0; i < this.selectedModel.filterProperties.length; i++) {
            let min = i;
            for (let j = i; j < this.selectedModel.filterProperties.length; j++) {
                if (this.selectedModel.filterProperties[j].friendlyName <
                    this.selectedModel.filterProperties[min].friendlyName) {
                    min = j;
                }
            }

            if (min !== i) {
                const t = this.selectedModel.filterProperties[min];
                this.selectedModel.filterProperties[min] = this.selectedModel.filterProperties[i];
                this.selectedModel.filterProperties[i] = t;
            }

        }
    }

    private dispatchEvent(nameEvent: string, data?: any): void {
        if (this.element && nameEvent) {
            const customEvent: CustomEvent = new CustomEvent(nameEvent, { bubbles: true, detail: data });
            this.element.dispatchEvent(customEvent);
        }
    }

    private ensureIsPropertySelectedObjectIsValid() {
        if (!this.isPropertySelected) {
            this.isPropertySelected = [];
        }
        if (this.isPropertySelected.length < this.clauses.length) {
            const clausesCount = this.clauses.length;
            const diff = this.clauses.length - this.isPropertySelected.length;
            for (let index = 0; index < diff; index++) {
                this.isPropertySelected.push({
                    property: this.clauses[clausesCount - diff + index].filterProperty,
                    isSelected: true
                })
            }
        }
    }

    private scrollToBottom() {
        if (this.queryBuilderEl && (this.queryBuilderEl.scrollHeight > this.queryBuilderEl.offsetHeight)) {
            this.queryBuilderEl.scrollTo({ top: this.queryBuilderEl.scrollHeight, behavior: 'smooth' });
        }
    }
}
